import {
  type ReactNode,
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  createElement,
  type PropsWithChildren,
} from "react";
import {
  fetchAdminMe,
  loginAdmin,
  setStoredToken,
  getStoredToken,
  UNAUTHORIZED_EVENT,
  type AuthUser,
  type LoginPayload,
  ApiRequestError,
} from "../lib/api";

interface AuthContextValue {
  user: AuthUser | null;
  token: string | null;
  loading: boolean;
  error: string | null;
  login: (payload: LoginPayload) => Promise<boolean>;
  logout: () => void;
  refresh: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: PropsWithChildren): ReactNode {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const clearAuthState = () => {
    setStoredToken(null);
    setToken(null);
    setUser(null);
  };

  const refresh = async () => {
    if (!token) {
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const me = await fetchAdminMe();
      setUser(me.user);
      setError(null);
    } catch (err: unknown) {
      clearAuthState();
      setError(err instanceof Error ? err.message : "Sitzung ungültig");
    } finally {
      setLoading(false);
    }
  };

  const login = async (payload: LoginPayload): Promise<boolean> => {
    setLoading(true);
    setError(null);
    try {
      const response = await loginAdmin(payload);
      setStoredToken(response.token);
      setToken(response.token);
      setUser(response.user);
      return true;
    } catch (err: unknown) {
      clearAuthState();
      if (err instanceof Error) {
        if (err instanceof ApiRequestError) {
          setError(err.message);
        } else {
          setError(err.message);
        }
      } else {
        setError("Login fehlgeschlagen");
      }
      return false;
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const initialToken = getStoredToken();
    if (initialToken) {
      setToken(initialToken);
      refresh();
    } else {
      setLoading(false);
    }

    const onUnauthorized = () => {
      clearAuthState();
    };
    window.addEventListener(UNAUTHORIZED_EVENT, onUnauthorized);
    return () => {
      window.removeEventListener(UNAUTHORIZED_EVENT, onUnauthorized);
    };
  }, []);

  const logout = () => {
    clearAuthState();
  };

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      token,
      loading,
      error,
      login,
      logout,
      refresh,
    }),
    [user, token, loading, error],
  );

  return createElement(AuthContext.Provider, { value }, children);
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
